/**
 * `/books` — books of account (ADR-0027, Phase 1).
 *
 * The thin general ledger made visible, in the Flint report-page idiom: a KPI
 * strip (accent-striped tiles) over Trial Balance · Profit & Loss · Balance
 * Sheet panels. Plus a manual journal voucher (depreciation, accruals, opening
 * balances) and a "Rebuild from documents" action.
 *
 * Chrome follows the compliance report pages (`/gst-returns`, `/tds`): page
 * head + actions, KpiTile strip, panel cards with icon-chip headers, app
 * report-table styling.
 */
import React, { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  DialogContent,
  DialogRoot,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
  cn,
  toast,
} from "@flint/ui";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  BookOpenCheck,
  Briefcase,
  Building2,
  Car,
  ChevronLeft,
  ChevronRight,
  Coins,
  CreditCard,
  Crown,
  Flag,
  Globe,
  Home,
  Landmark,
  Lock,
  LockOpen,
  Package,
  Percent,
  PiggyBank,
  Plus,
  Receipt,
  RefreshCw,
  RotateCcw,
  Scale,
  ShieldCheck,
  Trash2,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { trpc } from "../../../../lib/trpc.ts";
import { meQueryOptions } from "../../../../lib/session.ts";
import { usePermission } from "../../../../lib/permissions.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";

export const Route = createFileRoute("/(workspace)/compliance/books/")({
  component: BooksPage,
});

// Curated icon set for financial accounts — string name → component.
const ICON_MAP: Record<string, LucideIcon> = {
  Landmark, Wallet, CreditCard, Package, Users, Home, Briefcase, Car,
  Wrench, Globe, Receipt, PiggyBank, TrendingUp, TrendingDown, RotateCcw,
  Percent, Building2, Scale, Banknote, ArrowDownLeft, ArrowUpRight, Crown, Flag, Coins,
};

const ICON_OPTIONS: { name: string; Icon: LucideIcon }[] = Object.entries(ICON_MAP).map(
  ([name, Icon]) => ({ name, Icon }),
);

function inr(paise: string | bigint): string {
  const n = typeof paise === "bigint" ? paise : BigInt(paise);
  const neg = n < 0n;
  const abs = neg ? -n : n;
  const s = (Number(abs) / 100).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${neg ? "−" : ""}₹${s}`;
}

function BooksPage() {
  const canView = usePermission("books.view");
  if (!canView) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Books" }]}>
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-center text-muted">
          <BookOpenCheck size={36} className="opacity-30" />
          <p className="text-sm font-medium">You don't have access to Books.</p>
          <p className="text-xs">Ask a Founder to grant you the <em>books.view</em> permission.</p>
        </div>
      </WorkspaceShell>
    );
  }

  const utils = trpc.useUtils();
  const tb = trpc.accounting.trialBalance.useQuery({});
  const pl = trpc.accounting.profitAndLoss.useQuery({});
  const bs = trpc.accounting.balanceSheet.useQuery({});
  const obStatus = trpc.accounting.openingBalanceStatus.useQuery({});
  const [jvOpen, setJvOpen] = useState(false);
  const [obOpen, setObOpen] = useState(false);

  const rebuild = trpc.accounting.rebuild.useMutation({
    onSuccess: async (r) => {
      await Promise.all([tb.refetch(), pl.refetch(), bs.refetch()]);
      toast.success("Ledger rebuilt", {
        description: `${r.posted} posted · ${r.skipped} unchanged${r.failed ? ` · ${r.failed} failed` : ""}`,
      });
    },
    onError: (e) => toast.error("Rebuild failed", { description: e.message }),
  });

  const balanced = (tb.data?.isBalanced ?? true) && (bs.data?.isBalanced ?? true);

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Books" }]}>
      <div className="flex flex-col gap-5">
        {/* Page head */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-ink">Books of account</h1>
              <StatusPill tone={balanced ? "ok" : "bad"} size="xs" dot>
                {balanced ? "Balanced" : "Out of balance"}
              </StatusPill>
            </div>
            <p className="mt-1 text-sm text-muted">
              Double-entry ledger derived from your documents — Trial Balance, Profit &amp; Loss, and Balance Sheet.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              intent="ghost"
              size="sm"
              className="border border-line"
              disabled={rebuild.isPending}
              title="Re-post any documents missing from the ledger. New documents post automatically — use this to catch up history or retry a failed post."
              onClick={() => rebuild.mutate({})}
            >
              <RefreshCw size={14} className={rebuild.isPending ? "animate-spin" : ""} /> Re-sync
            </Button>
            <Button
              type="button"
              intent="ghost"
              size="sm"
              className="border border-line"
              disabled={obStatus.data?.posted ?? false}
              title={
                obStatus.data?.posted
                  ? `Opening balance posted as at ${obStatus.data.asOfDate}`
                  : "Enter your account balances at go-live — the difference plugs to Opening Balance Equity."
              }
              onClick={() => setObOpen(true)}
            >
              <Flag size={14} /> {obStatus.data?.posted ? "Opening balance set" : "Set opening balance"}
            </Button>
            <Button type="button" intent="primary" size="sm" onClick={() => setJvOpen(true)}>
              <Plus size={14} /> New journal entry
            </Button>
          </div>
        </header>

        {/* KPI strip */}
        <section
          aria-label="Financial summary"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
        >
          <KpiTile
            accent={pl.data && BigInt(pl.data.netProfitPaise) < 0n ? "rose" : "emerald"}
            label={pl.data && BigInt(pl.data.netProfitPaise) < 0n ? "Net loss (FY)" : "Net profit (FY)"}
            value={pl.data ? inr(pl.data.netProfitPaise) : "—"}
            meta={pl.data ? `FY ${pl.data.fromDate.slice(0, 4)}–${pl.data.toDate.slice(2, 4)}` : null}
            icon={pl.data && BigInt(pl.data.netProfitPaise) < 0n ? <TrendingDown size={13} /> : <TrendingUp size={13} />}
          />
          <KpiTile
            accent="indigo"
            label="Total income (FY)"
            value={pl.data ? inr(pl.data.totalIncomePaise) : "—"}
            meta="This financial year"
            icon={<Coins size={13} />}
          />
          <KpiTile
            accent="indigo"
            label="Total assets"
            value={bs.data ? inr(bs.data.totalAssetsPaise) : "—"}
            meta={bs.data ? `As at ${bs.data.asOf}` : null}
            icon={<Landmark size={13} />}
          />
          <KpiTile
            accent="amber"
            label="Net worth"
            value={
              bs.data
                ? inr((BigInt(bs.data.totalEquityPaise) + BigInt(bs.data.currentEarningsPaise)).toString())
                : "—"
            }
            meta="Equity + earnings"
            icon={<Scale size={13} />}
          />
        </section>

        {/* Suspense warning — transactions Flint couldn't map to a real account
            land in Suspense (and balance, so the books stay balanced); surface
            them so the operator reclassifies with a journal entry. */}
        {(() => {
          const susp = tb.data?.rows.find((r) => r.code === "suspense");
          if (!susp || susp.balancePaise === "0") return null;
          return (
            <Alert
              intent="warn"
              title={`${inr(susp.balancePaise)} sitting in Suspense`}
              description="Some transactions couldn't be mapped to a specific account. Reclassify them with a journal entry so the statements read cleanly."
            />
          );
        })()}

        <TabsRoot defaultValue="tb">
          <TabsList>
            <TabsTrigger value="tb">Trial Balance</TabsTrigger>
            <TabsTrigger value="pl">Profit &amp; Loss</TabsTrigger>
            <TabsTrigger value="bs">Balance Sheet</TabsTrigger>
            <TabsTrigger value="coa">Chart of accounts</TabsTrigger>
            <TabsTrigger value="periods">Period lock</TabsTrigger>
          </TabsList>

          <TabsContent value="tb" className="pt-4">
            {tb.data ? <TrialBalanceView data={tb.data} /> : <Loading q={tb} />}
          </TabsContent>
          <TabsContent value="pl" className="pt-4">
            {pl.data ? <ProfitAndLossView data={pl.data} /> : <Loading q={pl} />}
          </TabsContent>
          <TabsContent value="bs" className="pt-4">
            {bs.data ? <BalanceSheetView data={bs.data} /> : <Loading q={bs} />}
          </TabsContent>
          <TabsContent value="coa" className="pt-4">
            <ChartOfAccountsView />
          </TabsContent>
          <TabsContent value="periods" className="pt-4">
            <PeriodLockView />
          </TabsContent>
        </TabsRoot>
      </div>

      <JournalVoucherDialog
        open={jvOpen}
        onOpenChange={setJvOpen}
        onPosted={() =>
          Promise.all([tb.refetch(), pl.refetch(), bs.refetch(), utils.accounting.invalidate()])
        }
      />

      <OpeningBalanceDialog
        open={obOpen}
        onOpenChange={setObOpen}
        onPosted={() =>
          Promise.all([
            tb.refetch(),
            pl.refetch(),
            bs.refetch(),
            obStatus.refetch(),
            utils.accounting.invalidate(),
          ])
        }
      />
    </WorkspaceShell>
  );
}

function Loading({ q }: { q: { isLoading: boolean; error: { message: string } | null } }) {
  if (q.error) return <Alert intent="bad" title="Couldn't load" description={q.error.message} />;
  return <p className="p-4 text-sm text-muted">Loading…</p>;
}

// ── KPI tile ──
type Accent = "indigo" | "emerald" | "amber" | "rose";
const ACCENT: Record<Accent, { bar: string; iconBg: string; iconText: string; valueTone: string }> = {
  indigo:  { bar: "bg-sky-500",      iconBg: "bg-sky-50",      iconText: "text-sky-600",      valueTone: "text-ink" },
  emerald: { bar: "bg-emerald-500",  iconBg: "bg-emerald-50",  iconText: "text-emerald-600",  valueTone: "text-emerald-700" },
  amber:   { bar: "bg-amber-500",    iconBg: "bg-amber-50",    iconText: "text-amber-600",    valueTone: "text-ink" },
  rose:    { bar: "bg-rose-500",     iconBg: "bg-rose-50",     iconText: "text-rose-600",     valueTone: "text-rose-700" },
};

function KpiTile({
  accent,
  label,
  value,
  meta,
  icon,
}: {
  accent: Accent;
  label: string;
  value: string;
  meta?: React.ReactNode;
  icon: React.ReactNode;
}) {
  const a = ACCENT[accent];
  return (
    <div className="relative overflow-hidden rounded-md border border-line bg-paper px-4 pb-3 pt-3.5 shadow-sm transition-all hover:-translate-y-px hover:border-line-2 hover:shadow-md">
      {/* Top accent bar replaces the thin left stripe */}
      <span className={`absolute inset-x-0 top-0 h-[3px] ${a.bar}`} aria-hidden />
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="truncate text-xs font-semibold uppercase tracking-wider text-muted">
          {label}
        </span>
        <span className={`inline-grid h-7 w-7 place-items-center rounded-md ${a.iconBg} ${a.iconText}`}>
          {icon}
        </span>
      </div>
      <div className="num text-2xl font-bold leading-none tracking-tight text-ink">{value}</div>
      {meta ? <div className="mt-1 text-xs text-muted">{meta}</div> : null}
    </div>
  );
}

// ── Statement panel + table primitives ──
function Panel({
  icon: _icon,
  title,
  badge,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex items-center justify-between gap-2 border-b border-line bg-bg/50 px-4 py-3">
        <h2 className="text-sm font-bold tracking-tight text-ink">{title}</h2>
        {badge}
      </header>
      {children}
    </div>
  );
}

function StatementTable({ children }: { children: React.ReactNode }) {
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b-2 border-line-2 bg-bg text-xs font-semibold uppercase tracking-widest text-muted">
          <th className="px-4 py-2.5 text-left">Account</th>
          <th className="px-4 py-2.5 text-right">Debit</th>
          <th className="px-4 py-2.5 text-right">Credit</th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function SectionRow({ label, separator }: { label: string; separator?: boolean }) {
  return (
    <tr className={cn("border-b border-line", separator && "border-t-2 border-t-line-2")}>
      <td colSpan={3} className="bg-bg px-4 py-2">
        <span className="text-xs font-bold uppercase tracking-widest text-ink-2">{label}</span>
      </td>
    </tr>
  );
}

function LineRow({
  label,
  debit,
  credit,
  indent,
  strong,
  top,
}: {
  label: string;
  debit?: string;
  credit?: string;
  indent?: boolean;
  strong?: boolean;
  top?: boolean;
}) {
  return (
    <tr className={cn(
      "border-b border-line last:border-b-0 transition-colors",
      strong ? "bg-bg/60" : "hover:bg-raised",
      top && "border-t-2 border-t-line-2",
    )}>
      <td className={cn("px-4 py-2.5 text-sm text-ink", indent && "pl-9", strong && "font-semibold")}>
        {label}
      </td>
      <td className={cn(
        "num px-4 py-2.5 text-right text-sm tabular-nums",
        strong ? "font-semibold text-ink" : debit ? "text-ink" : "text-muted/40",
      )}>
        {debit ?? "—"}
      </td>
      <td className={cn(
        "num px-4 py-2.5 text-right text-sm tabular-nums",
        strong ? "font-semibold text-ink" : credit ? "text-ink" : "text-muted/40",
      )}>
        {credit ?? "—"}
      </td>
    </tr>
  );
}

function TrialBalanceView({ data }: { data: TbData }) {
  return (
    <Panel
      icon={<Scale size={15} />}
      title="Trial Balance"
      badge={
        <StatusPill tone={data.isBalanced ? "ok" : "bad"} size="xs" dot>
          {data.isBalanced ? "Balanced" : "Out of balance"}
        </StatusPill>
      }
    >
      <StatementTable>
        {data.rows.map((r) => (
          <LineRow
            key={r.code}
            label={r.name}
            debit={r.debitPaise !== "0" ? inr(r.debitPaise) : ""}
            credit={r.creditPaise !== "0" ? inr(r.creditPaise) : ""}
          />
        ))}
        <LineRow label="Total" debit={inr(data.totalDebitPaise)} credit={inr(data.totalCreditPaise)} strong top />
      </StatementTable>
    </Panel>
  );
}

function ProfitAndLossView({ data }: { data: PlData }) {
  return (
    <Panel
      icon={<TrendingUp size={15} />}
      title="Profit &amp; Loss"
      badge={
        <span className="text-xs text-muted">
          {data.fromDate} → {data.toDate}
        </span>
      }
    >
      <table className="w-full">
        <tbody>
          <SectionRow label="Income" />
          {data.income.map((l) => (
            <LineRow key={l.code} label={l.name} credit={inr(l.amountPaise)} indent />
          ))}
          <LineRow label="Total income" credit={inr(data.totalIncomePaise)} strong />
          <SectionRow label="Expenses" separator />
          {data.expenses.map((l) => (
            <LineRow key={l.code} label={l.name} debit={inr(l.amountPaise)} indent />
          ))}
          <LineRow label="Total expenses" debit={inr(data.totalExpensePaise)} strong />
        </tbody>
      </table>
      <div className="flex items-center justify-between border-t-2 border-t-line-2 bg-bg px-4 py-3">
        <span className="text-sm font-bold tracking-tight text-ink">
          {BigInt(data.netProfitPaise) >= 0n ? "Net profit" : "Net loss"}
        </span>
        <span
          className={cn(
            "num text-xl font-bold tabular-nums",
            BigInt(data.netProfitPaise) >= 0n ? "text-emerald-700" : "text-rose-600",
          )}
        >
          {inr(data.netProfitPaise)}
        </span>
      </div>
    </Panel>
  );
}

function BalanceSheetView({ data }: { data: BsData }) {
  const liabEquityTotal = (
    BigInt(data.totalLiabilitiesPaise) +
    BigInt(data.totalEquityPaise) +
    BigInt(data.currentEarningsPaise)
  ).toString();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <StatusPill tone={data.isBalanced ? "ok" : "bad"} size="xs" dot>
          {data.isBalanced ? "Balanced" : "Out of balance"}
        </StatusPill>
        <span className="text-xs text-muted">as at {data.asOf}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Panel icon={<Landmark size={15} />} title="Assets">
          <table className="w-full">
            <tbody>
              {data.assets.map((l) => (
                <LineRow key={l.code} label={l.name} debit={inr(l.amountPaise)} indent />
              ))}
              <LineRow label="Total assets" debit={inr(data.totalAssetsPaise)} strong top />
            </tbody>
          </table>
        </Panel>
        <Panel icon={<Scale size={15} />} title="Liabilities &amp; Equity">
          <table className="w-full">
            <tbody>
              <SectionRow label="Liabilities" />
              {data.liabilities.map((l) => (
                <LineRow key={l.code} label={l.name} credit={inr(l.amountPaise)} indent />
              ))}
              <SectionRow label="Equity" />
              {data.equity.map((l) => (
                <LineRow key={l.code} label={l.name} credit={inr(l.amountPaise)} indent />
              ))}
              <LineRow label="Current period earnings" credit={inr(data.currentEarningsPaise)} indent />
              <LineRow label="Total liabilities + equity" credit={inr(liabEquityTotal)} strong top />
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}

// ── Period lock (ADR-0027 §7) ──
/** Indian FY start year for today: Apr..Mar, so Jan–Mar belongs to the prior year. */
function currentFyStartYear(): number {
  const d = new Date();
  return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
}

function PeriodLockView() {
  const utils = trpc.useUtils();
  const me = useQuery(meQueryOptions);
  const isFounder = me.data?.company?.role === "Founder";
  const [fyStartYear, setFyStartYear] = useState(currentFyStartYear);
  const periodsQ = trpc.accounting.periods.useQuery({ fyStartYear });

  const close = trpc.accounting.closePeriod.useMutation({
    onSuccess: async (p) => {
      await utils.accounting.periods.invalidate();
      toast.success(`Closed ${p.label}`, { description: "Posting into this month is now locked." });
    },
    onError: (e) => toast.error("Couldn't close period", { description: e.message }),
  });
  const reopen = trpc.accounting.reopenPeriod.useMutation({
    onSuccess: async (p) => {
      await utils.accounting.periods.invalidate();
      toast.success(`Re-opened ${p.label}`, { description: "Posting into this month is allowed again." });
    },
    onError: (e) => toast.error("Couldn't re-open period", { description: e.message }),
  });
  const busy = close.isPending || reopen.isPending;

  return (
    <Panel
      icon={<Lock size={15} />}
      title="Period lock"
      badge={
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous financial year"
            className="grid h-7 w-7 place-items-center rounded-sm text-muted hover:bg-bg hover:text-ink"
            onClick={() => setFyStartYear((y) => y - 1)}
          >
            <ChevronLeft size={15} />
          </button>
          <span className="num min-w-[92px] text-center text-xs font-semibold text-ink">
            FY {fyStartYear}–{String(fyStartYear + 1).slice(2)}
          </span>
          <button
            type="button"
            aria-label="Next financial year"
            className="grid h-7 w-7 place-items-center rounded-sm text-muted hover:bg-bg hover:text-ink"
            onClick={() => setFyStartYear((y) => y + 1)}
          >
            <ChevronRight size={15} />
          </button>
        </div>
      }
    >
      <p className="border-b border-line bg-bg/30 px-4 py-2.5 text-xs text-muted">
        Closing a month locks the ledger — no document or correction can post into it until you re-open it.
        {!isFounder ? " Only a Founder can close or re-open a period." : ""}
      </p>
      {periodsQ.data ? (
        <table className="w-full">
          <thead>
            <tr className="border-b border-line bg-bg text-xs uppercase tracking-wider text-muted">
              <th className="px-4 py-2.5 text-left font-semibold">Month</th>
              <th className="px-4 py-2.5 text-left font-semibold">Status</th>
              <th className="px-4 py-2.5 text-right font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {periodsQ.data.periods.map((p) => {
              const closed = p.status === "closed";
              return (
                <tr key={p.label} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-2.5 text-sm font-medium text-ink">{p.label}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill tone={closed ? "bad" : "ok"} size="xs" dot>
                      {closed ? "Locked" : "Open"}
                    </StatusPill>
                    {closed && p.closedAt ? (
                      <span className="ml-2 text-xs text-muted">
                        closed {p.closedAt.slice(0, 10)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      type="button"
                      intent="ghost"
                      size="sm"
                      className="border border-line"
                      disabled={!isFounder || busy}
                      title={isFounder ? undefined : "Founder role required"}
                      onClick={() =>
                        closed
                          ? reopen.mutate({ year: p.year, month: p.month })
                          : close.mutate({ year: p.year, month: p.month })
                      }
                    >
                      {closed ? (
                        <>
                          <LockOpen size={13} /> Re-open
                        </>
                      ) : (
                        <>
                          <Lock size={13} /> Close
                        </>
                      )}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <Loading q={periodsQ} />
      )}
    </Panel>
  );
}

// ── Journal voucher dialog ──
interface JvLine {
  accountCode: string;
  debit: string;
  credit: string;
}
const emptyLine = (): JvLine => ({ accountCode: "", debit: "", credit: "" });
const toPaise = (rupees: string): bigint => {
  const n = Number(rupees);
  return Number.isFinite(n) && n > 0 ? BigInt(Math.round(n * 100)) : 0n;
};

function JournalVoucherDialog({
  open,
  onOpenChange,
  onPosted,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onPosted: () => void;
}) {
  const accountsQ = trpc.accounting.chartAccounts.useQuery({}, { enabled: open });
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [narration, setNarration] = useState("");
  const [lines, setLines] = useState<JvLine[]>([emptyLine(), emptyLine()]);

  const post = trpc.accounting.postJournal.useMutation({
    onSuccess: () => {
      toast.success("Journal entry posted");
      onPosted();
      onOpenChange(false);
      setLines([emptyLine(), emptyLine()]);
      setNarration("");
    },
    onError: (e) => toast.error("Couldn't post", { description: e.message }),
  });

  const totals = useMemo(() => {
    let d = 0n;
    let c = 0n;
    for (const l of lines) {
      d += toPaise(l.debit);
      c += toPaise(l.credit);
    }
    return { d, c, balanced: d === c && d > 0n };
  }, [lines]);

  function patch(i: number, p: Partial<JvLine>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...p } : l)));
  }

  function submit() {
    const payload = lines
      .filter((l) => l.accountCode && (toPaise(l.debit) > 0n || toPaise(l.credit) > 0n))
      .map((l) => ({
        accountCode: l.accountCode,
        debitPaise: toPaise(l.debit).toString(),
        creditPaise: toPaise(l.credit).toString(),
      }));
    if (payload.length < 2) {
      toast.error("Add at least two lines");
      return;
    }
    post.mutate({ entryDate, narration: narration.trim() || "Journal entry", lines: payload });
  }

  const accounts = accountsQ.data?.accounts ?? [];

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] p-0 flex flex-col max-h-[92vh]">
        <header className="flex items-center gap-3 border-b border-line px-5 py-4">
          <span
            aria-hidden
            className="inline-grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-sky-50 text-sky-700"
          >
            <Plus size={15} strokeWidth={2} />
          </span>
          <div>
            <DialogTitle className="text-sm font-semibold leading-tight text-ink">
              New journal entry
            </DialogTitle>
            <p className="mt-0.5 text-xs text-muted">
              For entries with no document — depreciation, accruals, opening balances, corrections.
            </p>
          </div>
        </header>
        <div className="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-[160px_1fr] gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-ink-2">Date</span>
              <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.currentTarget.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-ink-2">Narration</span>
              <Input value={narration} onChange={(e) => setNarration(e.currentTarget.value)} placeholder="e.g. May depreciation" />
            </label>
          </div>

          <div className="grid grid-cols-[1fr_110px_110px_28px] gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
            <span>Account</span>
            <span className="text-right">Debit ₹</span>
            <span className="text-right">Credit ₹</span>
            <span />
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_110px_28px] items-center gap-2">
              <Select value={l.accountCode} onValueChange={(v) => patch(i, { accountCode: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.code} value={a.code}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="text-right"
                inputMode="decimal"
                value={l.debit}
                onChange={(e) => patch(i, { debit: e.currentTarget.value, credit: "" })}
                placeholder="0.00"
              />
              <Input
                className="text-right"
                inputMode="decimal"
                value={l.credit}
                onChange={(e) => patch(i, { credit: e.currentTarget.value, debit: "" })}
                placeholder="0.00"
              />
              <button
                type="button"
                aria-label="Remove line"
                className="grid h-7 w-7 place-items-center rounded-sm text-muted hover:bg-bad-50 hover:text-bad"
                onClick={() => setLines((ls) => (ls.length > 2 ? ls.filter((_, idx) => idx !== i) : ls))}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <Button
            type="button"
            intent="ghost"
            size="sm"
            className="self-start border border-line"
            onClick={() => setLines((ls) => [...ls, emptyLine()])}
          >
            <Plus size={13} /> Add line
          </Button>

          <div className="mt-1 flex items-center justify-between rounded-sm bg-bg px-3 py-2 text-sm">
            <span className="text-muted">
              Debit {inr(totals.d.toString())} · Credit {inr(totals.c.toString())}
            </span>
            <StatusPill tone={totals.balanced ? "ok" : "muted"} size="xs" dot>
              {totals.balanced ? "Balanced" : "Not balanced"}
            </StatusPill>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
          <Button type="button" intent="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            intent="primary"
            size="sm"
            disabled={!totals.balanced || post.isPending}
            onClick={submit}
          >
            {post.isPending ? "Posting…" : "Post entry"}
          </Button>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

// ── Opening balance dialog (ADR-0027 §5) ──
function OpeningBalanceDialog({
  open,
  onOpenChange,
  onPosted,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onPosted: () => void;
}) {
  const accountsQ = trpc.accounting.chartAccounts.useQuery({}, { enabled: open });
  const [asOfDate, setAsOfDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState<JvLine[]>([emptyLine(), emptyLine()]);

  const post = trpc.accounting.postOpeningBalance.useMutation({
    onSuccess: (r) => {
      const plug = BigInt(r.plugPaise);
      toast.success("Opening balance posted", {
        description:
          plug === 0n
            ? "Your balances netted to zero."
            : `${inr(plug < 0n ? -plug : plug)} plugged to Opening Balance Equity.`,
      });
      onPosted();
      onOpenChange(false);
      setLines([emptyLine(), emptyLine()]);
    },
    onError: (e) => toast.error("Couldn't post opening balance", { description: e.message }),
  });

  // The difference (debit − credit) auto-plugs to Opening Balance Equity.
  const plug = useMemo(() => {
    let d = 0n;
    let c = 0n;
    for (const l of lines) {
      d += toPaise(l.debit);
      c += toPaise(l.credit);
    }
    return d - c;
  }, [lines]);

  function patch(i: number, p: Partial<JvLine>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...p } : l)));
  }

  function submit() {
    const payload = lines
      .filter((l) => l.accountCode && (toPaise(l.debit) > 0n || toPaise(l.credit) > 0n))
      .map((l) => ({
        accountCode: l.accountCode,
        debitPaise: toPaise(l.debit).toString(),
        creditPaise: toPaise(l.credit).toString(),
      }));
    if (payload.length < 1) {
      toast.error("Enter at least one account balance");
      return;
    }
    post.mutate({ asOfDate, lines: payload });
  }

  // Hide Opening Balance Equity from the picker — it's the auto-plug target.
  const accounts = (accountsQ.data?.accounts ?? []).filter((a) => a.code !== "opening_balance_equity");

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] p-0 flex flex-col max-h-[92vh]">
        <header className="flex items-center gap-3 border-b border-line px-5 py-4">
          <span
            aria-hidden
            className="inline-grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-amber-50 text-amber-700"
          >
            <Flag size={15} strokeWidth={2} />
          </span>
          <div>
            <DialogTitle className="text-sm font-semibold leading-tight text-ink">
              Opening balance
            </DialogTitle>
            <p className="mt-0.5 text-xs text-muted">
              Your account balances at go-live. The difference plugs to Opening Balance Equity —
              reconcile it later. This is posted once.
            </p>
          </div>
        </header>
        <div className="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto px-5 py-4">
          <label className="flex max-w-[200px] flex-col gap-1">
            <span className="text-xs font-semibold text-ink-2">As at date</span>
            <Input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.currentTarget.value)} />
          </label>

          <div className="grid grid-cols-[1fr_110px_110px_28px] gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
            <span>Account</span>
            <span className="text-right">Debit ₹</span>
            <span className="text-right">Credit ₹</span>
            <span />
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_110px_28px] items-center gap-2">
              <Select value={l.accountCode} onValueChange={(v) => patch(i, { accountCode: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.code} value={a.code}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="text-right"
                inputMode="decimal"
                value={l.debit}
                onChange={(e) => patch(i, { debit: e.currentTarget.value, credit: "" })}
                placeholder="0.00"
              />
              <Input
                className="text-right"
                inputMode="decimal"
                value={l.credit}
                onChange={(e) => patch(i, { credit: e.currentTarget.value, debit: "" })}
                placeholder="0.00"
              />
              <button
                type="button"
                aria-label="Remove line"
                className="grid h-7 w-7 place-items-center rounded-sm text-muted hover:bg-bad-50 hover:text-bad"
                onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls))}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <Button
            type="button"
            intent="ghost"
            size="sm"
            className="self-start border border-line"
            onClick={() => setLines((ls) => [...ls, emptyLine()])}
          >
            <Plus size={13} /> Add line
          </Button>

          <div className="mt-1 flex items-center justify-between rounded-sm bg-amber-50 px-3 py-2 text-sm">
            <span className="text-ink-2">Opening Balance Equity (auto-plug)</span>
            <span className="num font-semibold text-ink">
              {plug === 0n ? "—" : inr(plug < 0n ? (-plug).toString() : plug.toString())}
              {plug !== 0n ? <span className="ml-1 text-xs text-muted">{plug > 0n ? "Cr" : "Dr"}</span> : null}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
          <Button type="button" intent="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" intent="primary" size="sm" disabled={post.isPending} onClick={submit}>
            {post.isPending ? "Posting…" : "Post opening balance"}
          </Button>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

// ── Chart of accounts ──
const TYPE_LABEL: Record<string, string> = {
  asset: "Asset",
  liability: "Liability",
  equity: "Equity",
  income: "Income",
  expense: "Expense",
};

const TYPE_ORDER = ["asset", "liability", "equity", "income", "expense"];

function ChartOfAccountsView() {
  const utils = trpc.useUtils();
  const accountsQ = trpc.accounting.chartAccounts.useQuery({});
  const [createOpen, setCreateOpen] = useState(false);

  const del = trpc.accounting.deleteAccount.useMutation({
    onSuccess: async (_, vars) => {
      await utils.accounting.chartAccounts.invalidate();
      toast.success(`Account deleted`, { description: vars.code });
    },
    onError: (e) => toast.error("Couldn't delete account", { description: e.message }),
  });

  const grouped = useMemo(() => {
    const all = accountsQ.data?.accounts ?? [];
    const map = new Map<string, typeof all>();
    for (const type of TYPE_ORDER) map.set(type, []);
    for (const a of all) {
      if (!map.has(a.type)) map.set(a.type, []);
      map.get(a.type)!.push(a);
    }
    return map;
  }, [accountsQ.data]);

  return (
    <>
      <Panel
        icon={<BookOpenCheck size={15} />}
        title="Chart of accounts"
        badge={
          <Button type="button" intent="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus size={13} /> New account
          </Button>
        }
      >
        <p className="px-4 py-2.5 text-xs text-muted bg-bg/30 border-b border-line">
          System accounts are locked. Custom accounts can be deleted if they have no posted transactions.
        </p>
        {accountsQ.data ? (
          <table className="w-full">
            <thead>
              <tr className="border-b border-line bg-bg text-xs uppercase tracking-wider text-muted">
                <th className="px-4 py-2.5 text-left font-semibold">Account name</th>
                <th className="px-4 py-2.5 text-left font-semibold">Code</th>
                <th className="px-4 py-2.5 text-right font-semibold" />
              </tr>
            </thead>
            <tbody>
              {TYPE_ORDER.map((type) => {
                const rows = grouped.get(type) ?? [];
                if (rows.length === 0) return null;
                return (
                  <React.Fragment key={type}>
                    <tr className="border-b border-line">
                      <td
                        colSpan={3}
                        className="bg-bg px-4 py-2"
                      >
                        <span className="text-xs font-bold uppercase tracking-widest text-ink-2">
                          {TYPE_LABEL[type]}
                        </span>
                      </td>
                    </tr>
                    {rows.map((a) => {
                      const bankAccount = a.code.startsWith("bank_account:");
                      const IconComp = a.icon
                        ? ICON_MAP[a.icon]
                        : bankAccount
                          ? Landmark
                          : null;
                      return (
                      <tr
                        key={a.code}
                        className="border-b border-line last:border-b-0 transition-colors hover:bg-raised"
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "inline-grid h-6 w-6 shrink-0 place-items-center rounded-sm",
                              IconComp ? "bg-bg text-muted" : "text-muted/20",
                            )}>
                              {IconComp ? <IconComp size={13} /> : null}
                            </span>
                            <span className="text-sm text-ink">{a.name}</span>
                            {a.isSystem ? (
                              <span className="inline-flex items-center gap-0.5 text-xs text-muted/60">
                                <ShieldCheck size={10} />
                                system
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted">{a.code}</td>
                        <td className="px-4 py-2.5 text-right">
                          {!a.isSystem ? (
                            <button
                              type="button"
                              aria-label={`Delete ${a.name}`}
                              className="grid h-7 w-7 place-items-center rounded-sm text-muted hover:bg-bad-50 hover:text-bad"
                              disabled={del.isPending}
                              onClick={() => del.mutate({ code: a.code })}
                            >
                              <Trash2 size={13} />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Loading q={accountsQ} />
        )}
      </Panel>

      <CreateAccountDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => utils.accounting.chartAccounts.invalidate()}
      />
    </>
  );
}

function CreateAccountDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [type, setType] = useState<string>("");
  const [codeEdited, setCodeEdited] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);

  function toSlug(s: string) {
    return s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "")
      .slice(0, 64);
  }

  function handleNameChange(v: string) {
    setName(v);
    if (!codeEdited) setCode(toSlug(v));
  }

  const create = trpc.accounting.createAccount.useMutation({
    onSuccess: (r) => {
      toast.success("Account created", { description: r.name });
      onCreated();
      onOpenChange(false);
      setName("");
      setCode("");
      setType("");
      setCodeEdited(false);
      setSelectedIcon(null);
    },
    onError: (e) => toast.error("Couldn't create account", { description: e.message }),
  });

  const normalBalance =
    type === "asset" || type === "expense" ? "Debit" : type ? "Credit" : "—";

  const valid = name.trim().length > 0 && /^[a-z0-9_]+$/.test(code) && type !== "";

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-0">
        <header className="flex items-center gap-3 border-b border-line px-5 py-4">
          <span
            aria-hidden
            className="inline-grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-sky-50 text-sky-700"
          >
            <Plus size={15} strokeWidth={2} />
          </span>
          <div>
            <DialogTitle className="text-sm font-semibold leading-tight text-ink">
              New account
            </DialogTitle>
            <p className="mt-0.5 text-xs text-muted">
              Add a custom ledger account to your chart.
            </p>
          </div>
        </header>
        <div className="flex flex-col gap-4 px-5 py-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-ink-2">Account name</span>
            <Input
              value={name}
              onChange={(e) => handleNameChange(e.currentTarget.value)}
              placeholder="e.g. Share Capital"
              autoFocus
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-ink-2">
              Code{" "}
              <span className="font-normal text-muted">(auto-generated — letters, digits, underscores)</span>
            </span>
            <Input
              value={code}
              onChange={(e) => {
                setCode(e.currentTarget.value);
                setCodeEdited(true);
              }}
              placeholder="e.g. share_capital"
              className="font-mono text-sm"
            />
          </label>

          <div className="flex gap-3">
            <label className="flex flex-1 flex-col gap-1.5">
              <span className="text-xs font-semibold text-ink-2">Type</span>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asset">Asset</SelectItem>
                  <SelectItem value="liability">Liability</SelectItem>
                  <SelectItem value="equity">Equity</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </label>

            <div className="flex flex-1 flex-col gap-1.5">
              <span className="text-xs font-semibold text-ink-2">Normal balance</span>
              <div className="flex h-9 items-center rounded-md border border-line bg-bg px-3 text-sm text-muted">
                {normalBalance}
                {type ? (
                  <span className="ml-1 text-xs text-muted/60">
                    (auto)
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-ink-2">
              Icon{" "}
              <span className="font-normal text-muted">(optional)</span>
            </span>
            <div className="flex flex-wrap gap-1.5 rounded-md border border-line bg-bg p-2">
              <button
                type="button"
                title="No icon"
                onClick={() => setSelectedIcon(null)}
                className={cn(
                  "inline-grid h-7 w-7 place-items-center rounded-sm text-xs text-muted transition-colors",
                  selectedIcon === null
                    ? "bg-sky-100 text-sky-700 ring-1 ring-sky-400"
                    : "hover:bg-raised",
                )}
              >
                —
              </button>
              {ICON_OPTIONS.map(({ name: iconName, Icon }) => (
                <button
                  key={iconName}
                  type="button"
                  title={iconName}
                  onClick={() => setSelectedIcon(iconName)}
                  className={cn(
                    "inline-grid h-7 w-7 place-items-center rounded-sm transition-colors",
                    selectedIcon === iconName
                      ? "bg-sky-100 text-sky-700 ring-1 ring-sky-400"
                      : "text-muted hover:bg-raised hover:text-ink",
                  )}
                >
                  <Icon size={14} />
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
          <Button type="button" intent="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            intent="primary"
            size="sm"
            disabled={!valid || create.isPending}
            onClick={() =>
              create.mutate({
                code,
                name: name.trim(),
                type: type as "asset" | "liability" | "equity" | "income" | "expense",
                icon: selectedIcon,
              })
            }
          >
            {create.isPending ? "Creating…" : "Create account"}
          </Button>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

// Wire types (mirror the contract outputs).
type TbData = {
  rows: { code: string; name: string; type: string; debitPaise: string; creditPaise: string; balancePaise: string }[];
  totalDebitPaise: string;
  totalCreditPaise: string;
  isBalanced: boolean;
};
type StmtLine = { code: string; name: string; amountPaise: string };
type PlData = {
  fromDate: string;
  toDate: string;
  income: StmtLine[];
  totalIncomePaise: string;
  expenses: StmtLine[];
  totalExpensePaise: string;
  netProfitPaise: string;
};
type BsData = {
  asOf: string;
  assets: StmtLine[];
  totalAssetsPaise: string;
  liabilities: StmtLine[];
  totalLiabilitiesPaise: string;
  equity: StmtLine[];
  totalEquityPaise: string;
  currentEarningsPaise: string;
  isBalanced: boolean;
};
